// Copyright (c) 2019 Uber Technologies, Inc.

/* global WebSocket,ArrayBuffer */
/* eslint-disable camelcase */
import assert from "assert";
import PromiseRetry from "promise-retry";

import { XVIZStreamBuffer, parseStreamMessage } from "@xviz/parser";
import XVIZController from "./xviz-controller-v2";
import { _XVIZLoaderInterface as XVIZLoaderInterface } from "streetscape.gl";

/**
 * Connect to XVIZ 2 websocket manage storage of XVIZ data into a XVIZStreamBuffer
 *
 * This class is a Websocket base class and is expected to be subclassed with
 * the following methods overridden:
 *
 * - _onOpen()
 */
export default class XVIZWebsocketLoader extends XVIZLoaderInterface {
	/**
	 * constructor
	 * @params serverConfig {object}
	 *   - serverConfig.serverUrl {string}
	 *   - serverConfig.defaultLogLength {number, optional} - default 30
	 *   - serverConfig.queryParams {object, optional}
	 *   - serverConfig.retryAttempts {number, optional} - default 3
	 * @params worker {string|function, optional}
	 * @params maxConcurrency {number, optional} - default 3
	 * @params debug {function} - Debug callback for the XVIZ parser.
	 * @params logGuid {string}
	 * @params logProfile {string, optional}
	 * @params duration {number, optional}
	 * @params timestamp {number, optional}
	 * @params bufferLength {number, optional}
	 */
	constructor(options = {}) {
		super(options);

		this.socket = null;

		this.retrySettings = {
			retries: 3,
			minTimeout: 500,
			randomize: true,
		};

		this.streamBuffer = new XVIZStreamBuffer();

		// Handler object for the websocket events
		// Note: needs to be last due to member dependencies
		this.WebSocketClass = options.WebSocketClass || WebSocket;
	}

	isOpen() {
		return this.socket; // && this.socket.readyState === WEB_SOCKET_OPEN_STATE;
	}

	seek(timestamp) {
		super.seek(timestamp);
	}

	/**
	 * Open an XVIZ socket connection with automatic retry
	 *
	 * @returns {Promise} WebSocket connection
	 */
	connect() {
		assert(this.socket === null, "Socket Manager still connected");
		return this.attemptConnection();
	}

	attemptConnection() {
		const { url } = this.requestParams;

		return new Promise((resolve, reject) => {
			try {
				const ws = new this.WebSocketClass(url);
				ws.binaryType = "arraybuffer";

				ws.onmessage = (message) => {
					const hasMetadata = Boolean(this.getMetadata());

					return parseStreamMessage({
						message: message.data,
						onResult: this.onXVIZMessage,
						onError: this.onError,
						debug: this._debug.bind(this, "parse_message"),
						worker: hasMetadata && this.options.worker,
						maxConcurrency: this.options.maxConcurrency,
					});
				};

				ws.onerror = this.onError;
				ws.onclose = (event) => {
					this._onWSClose(event);
					if (this.socket !== null) {
						this.socket = null;
						this._onClosed();
					}
					reject(event);
				};

				// On success, resolve the promise with the now ready socket
				ws.onopen = () => {
					this.socket = ws;
					this._onWSOpen();
					resolve(ws);
				};
			} catch (err) {
				reject(err);
			}
		}).catch((event) => {
			setTimeout(() => {
				this.attemptConnection();
			}, this.retrySettings.minTimeout);
		});
	}

	close() {
		if (this.socket) {
			this.socket.close();
			this.socket = null;
		}
	}

	_onClosed = () => {
		// should be overrided
	};

	// Subclasses *MUST* implement these methods
	_onOpen() {
		throw new Error("_onOpen() method must be overridden");
	}

	// PRIVATE Methods

	// Notifications and metric reporting
	_onWSOpen = () => {
		// Request data if we are restarting, otherwise wait for metadata
		// TODO - protocol negotiation
		this.xvizHandler = new XVIZController(this.socket);

		this._debug("socket_open", this.requestParams);
		this._onOpen();
	};

	_onWSClose = (event) => {
		// Only called on connection closure, which would be an error case
		this._debug("socket_closed", event);
	};

	_onWSError = (event) => {
		this._debug("socket_error", event);
	};
}
